// src/components/Admin/DocumentUpload.tsx
import React, { useState, useContext } from 'react';
import { Box, TextField, Button, Typography, Paper, Alert, AlertTitle, CircularProgress } from '@mui/material';
import { AuthContext } from '../../contexts/AuthContext';
import { uploadDocument } from '../../api/adminService';

const DocumentUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { username, password } = useContext(AuthContext);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    if (!file || !title) {
      setError('Por favor selecciona un archivo y proporciona un título');
      return;
    }
    
    if (!username || !password) {
      setError('No estás autenticado correctamente');
      return;
    }
    
    setLoading(true);
    
    try {
      await uploadDocument(file, title, author, source || file.name, username, password);
      setSuccess(true);
      
      // Reset form
      setFile(null);
      setTitle('');
      setAuthor('');
      setSource('');
      
      if (document.getElementById('file-upload') as HTMLInputElement) {
        (document.getElementById('file-upload') as HTMLInputElement).value = '';
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Subir Documento
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Éxito</AlertTitle>
          Documento subido correctamente
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            component="label"
            fullWidth
          >
            Seleccionar Archivo
            <input
              id="file-upload"
              type="file"
              accept=".pdf,.txt"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Archivo seleccionado: {file.name}
            </Typography>
          )}
        </Box>
        
        <TextField
          fullWidth
          margin="normal"
          label="Título"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="Autor"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="Fuente"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          helperText="Si no se especifica, se usará el nombre del archivo"
        />
        
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          disabled={loading || !file || !title}
        >
          {loading ? <CircularProgress size={24} /> : 'Subir Documento'}
        </Button>
      </Box>
    </Paper>
  );
};

export default DocumentUpload;